import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './TopicsNav.css'

const IconForward = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18" />
  </svg>
)

const IconChevron = ({ up }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {up ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
  </svg>
)

const IconEllipsis = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="19" cy="12" r="1.6" />
  </svg>
)

function RowMenu({ open, onToggle, onAddChild, onDuplicate, onRename, onDelete }) {
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const [pos, setPos] = useState(null)

  const place = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (!rect) return
    const menuHeight = 160
    const below = rect.bottom + 4
    const top = below + menuHeight > window.innerHeight ? Math.max(8, rect.top - menuHeight - 4) : below
    setPos({ top, left: rect.right })
  }, [])

  useLayoutEffect(() => {
    if (!open) return undefined
    place()
    const handler = () => place()
    window.addEventListener('resize', handler)
    window.addEventListener('scroll', handler, true)
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('scroll', handler, true)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (event) => {
      if (menuRef.current?.contains(event.target)) return
      if (btnRef.current?.contains(event.target)) return
      onToggle(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') onToggle(false)
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onToggle])

  const menu = open && pos && (
    <div
      className="topic-nav-menu"
      ref={menuRef}
      role="menu"
      style={{ top: pos.top, left: pos.left }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <button type="button" role="menuitem" onClick={onAddChild}>
        Add child
      </button>
      <button type="button" role="menuitem" onClick={onDuplicate}>
        Duplicate
      </button>
      <button type="button" role="menuitem" onClick={onRename}>
        Rename
      </button>
      <button type="button" role="menuitem" className="is-danger" onClick={onDelete}>
        Delete
      </button>
    </div>
  )

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={'topic-nav-forward-btn' + (open ? ' is-on' : '')}
        title="More"
        aria-label="More"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation()
          onToggle(!open)
        }}
      >
        <IconEllipsis />
      </button>
      {menu && createPortal(menu, document.body)}
    </>
  )
}

// Topics sidebar. Label click selects the topic. The arrow burrows into its
// children (segmented). The chevron folds a subtree (collapsed).
function TopicsNav({
  status,
  rows,
  atRoot,
  mode,
  selectedId,
  hasChildren,
  depthOf,
  isExpanded,
  onOpen,
  onForward,
  onToggleCollapse,
  titleOf,
  searching,
  onAddChild,
  onDuplicate,
  onRename,
  onDelete,
  scrollToId,
  scrollNonce,
}) {
  const [menuId, setMenuId] = useState(null)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!scrollToId) return
    const el = rootRef.current?.querySelector(`[data-topic-nav-id="${scrollToId}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [scrollNonce, scrollToId])
  if (status === 'loading') return <div className="topic-nav-status">Loading…</div>
  if (status === 'error') return <div className="topic-nav-status">Could not load topics.</div>
  if (!rows.length) {
    const empty = searching ? 'No matching topics.' : atRoot ? 'No topics yet.' : 'No topics here.'
    return <div className="topic-nav-status">{empty}</div>
  }

  return (
    <div className="topic-nav" role="tree" aria-label="Topics" ref={rootRef}>
      {rows.map((row) => {
        const parent = hasChildren(row)
        const showForward = mode === 'segmented' && parent
        const showChevron = mode === 'collapsed' && parent
        const expanded = showChevron && isExpanded(row)
        const depth = depthOf ? depthOf(row) : 0
        const selected = String(row._id) === String(selectedId)
        return (
          <div
            key={row._id}
            data-topic-nav-id={row._id}
            className={'topic-nav-row' + (selected ? ' is-selected' : '')}
            style={{ '--topic-nav-level': depth }}
            role="treeitem"
            aria-selected={selected}
            onClick={() => onOpen?.(row)}
          >
            <div className="topic-nav-label" title={titleOf ? titleOf(row) : row.name}>
              {row.name}
            </div>
            <div className="topic-nav-count">{row.count || 0}</div>
            <div className="topic-nav-more">
              <RowMenu
                open={menuId === String(row._id)}
                onToggle={(next) => setMenuId(next ? String(row._id) : null)}
                onAddChild={() => {
                  setMenuId(null)
                  onAddChild?.(row)
                }}
                onDuplicate={() => {
                  setMenuId(null)
                  onDuplicate?.(row)
                }}
                onRename={() => {
                  setMenuId(null)
                  onRename?.(row)
                }}
                onDelete={() => {
                  setMenuId(null)
                  onDelete?.(row)
                }}
              />
            </div>
            <div className="topic-nav-forward">
              {showForward && (
                <button
                  type="button"
                  className="topic-nav-forward-btn"
                  title="See topics inside"
                  aria-label={`See topics inside ${row.name}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    onForward?.(row)
                  }}
                >
                  <IconForward />
                </button>
              )}
              {showChevron && (
                <button
                  type="button"
                  className="topic-nav-forward-btn"
                  title={expanded ? 'Collapse' : 'Expand'}
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                  aria-expanded={expanded}
                  onClick={(event) => {
                    event.stopPropagation()
                    onToggleCollapse?.(row)
                  }}
                >
                  <IconChevron up={!expanded} />
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TopicsNav
